/**
 * TECH-2730. GSM-7 / UCS-2 segment counting for the SMS body.
 *
 * Nothing in this repository does this — verified by search across the whole
 * tree — and no SMS-encoding library is a dependency, so this is written
 * rather than wrapped.
 *
 * WHY IT MATTERS HERE. `content` is the SMS body for pep_signup and
 * pep_login. An author writing in Hebrew or Chinese silently crosses into
 * UCS-2, where the limit drops from 160 characters to 70, so wording that
 * looks the same length as its English original can cost three times as much
 * to send and arrive split. Nothing in the product told them.
 *
 * THIS COUNTS, IT DOES NOT BLOCK. Consistent with the decision taken twice in
 * review for the missing-SMS warning: tell the author, do not refuse the save.
 */

/**
 * The GSM 03.38 basic alphabet. Anything outside it either costs two septets
 * (the extension table below) or forces the whole message to UCS-2.
 */
const GSM7_BASIC =
  '@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !"#¤%&\'()*+,-./0123456789:;<=>?' +
  '¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà';

/**
 * Characters that exist in GSM-7 only via an escape, so each costs TWO
 * septets. Missing these undercounts a message that uses square brackets or
 * a euro sign — common in a link-bearing SMS.
 */
const GSM7_EXTENDED = '^{}\\[~]|€';

const GSM7_BASIC_SET = new Set(GSM7_BASIC);
const GSM7_EXTENDED_SET = new Set(GSM7_EXTENDED);

/** Single-message and concatenated-part limits, per encoding. */
const LIMITS = {
  'GSM-7': { single: 160, concatenated: 153 },
  'UCS-2': { single: 70, concatenated: 67 },
};

/**
 * Whether every character fits GSM-7.
 *
 * Iterated by code POINT, not code unit: an emoji or any astral character is
 * a surrogate pair, and stepping by code unit would test each half separately
 * and mis-measure the length.
 */
export const isGsm7 = (text) =>
  [...(text || '')].every(
    (character) =>
      GSM7_BASIC_SET.has(character) || GSM7_EXTENDED_SET.has(character),
  );

/**
 * Billable units. GSM-7 charges two septets for an extended character; UCS-2
 * charges two units for an astral character, because it is a surrogate pair
 * on the wire.
 */
const billableUnits = (text, encoding) =>
  [...(text || '')].reduce((total, character) => {
    if (encoding === 'GSM-7') {
      return total + (GSM7_EXTENDED_SET.has(character) ? 2 : 1);
    }
    return total + (character.codePointAt(0) > 0xffff ? 2 : 1);
  }, 0);

/**
 * QA round 5, issue 2b (TECH-3955). The SMS does not carry
 * {{linkToShorten}}: messaging-apigateway replaces it with
 * `${SHORTENER_URL}/l/<12-character id>` (messaging-shortener slices the id
 * to 12). Count that, not the 17-character placeholder. Without a
 * shortenerUrl the text is counted as written, as before.
 */
const LINK_PLACEHOLDER = /\{\{\{?\s*linkToShorten\s*\}?\}\}/g;
const SHORT_LINK_ID_LENGTH = 12;

const shortLinkSample = (shortenerUrl) =>
  `${shortenerUrl.replace(/\/+$/, '')}/l/${'x'.repeat(SHORT_LINK_ID_LENGTH)}`;

export const withShortLink = (text, shortenerUrl) =>
  shortenerUrl
    ? (text || '').replace(LINK_PLACEHOLDER, shortLinkSample(shortenerUrl))
    : text;

/**
 * The length one {{linkToShorten}} is counted as, or null when the text has
 * none or no shortenerUrl is configured -- shown next to the counter.
 */
export const shortLinkLength = (text, shortenerUrl) =>
  shortenerUrl && (text || '').search(LINK_PLACEHOLDER) !== -1
    ? shortLinkSample(shortenerUrl).length
    : null;

/**
 * @param {string} text
 * @param {{shortenerUrl?: string}} [options]
 * @returns {{encoding: 'GSM-7'|'UCS-2', units: number, segments: number, limit: number, remaining: number}}
 */
export const smsSegments = (text, { shortenerUrl } = {}) => {
  const value = withShortLink(text, shortenerUrl) || '';
  const encoding = isGsm7(value) ? 'GSM-7' : 'UCS-2';
  const { single, concatenated } = LIMITS[encoding];
  const units = billableUnits(value, encoding);

  // An empty body is not a message. Reporting 1 segment for it would tell an
  // author their blank field already costs a send.
  if (units === 0) {
    return {
      encoding,
      units: 0,
      segments: 0,
      limit: single,
      remaining: single,
    };
  }

  if (units <= single) {
    return {
      encoding,
      units,
      segments: 1,
      limit: single,
      remaining: single - units,
    };
  }

  // Past one message every part carries a concatenation header, so the
  // per-part allowance drops — which is why 161 GSM-7 characters is two
  // segments of 153, not 160 plus 1.
  const segments = Math.ceil(units / concatenated);
  return {
    encoding,
    units,
    segments,
    limit: concatenated * segments,
    remaining: concatenated * segments - units,
  };
};
